import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { stockTransfersAPI } from '../../api/stockTransfers';
import { warehousesAPI } from '../../api/warehouses';
import { dbHelpers } from '../../db/database';
import { filterWarehouses } from '../../utils/permissions';
import syncService from '../../services/syncService';
import './StockTransfer.css';

function StockTransfer() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    product: '',
    from_warehouse: '',
    to_warehouse: '',
    quantity: 1,
    notes: ''
  });
  const [availableStock, setAvailableStock] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Update available stock when product or source warehouse changes
    if (formData.product && formData.from_warehouse) {
      // console.log('Looking for stock:', {
      //   product: formData.product,
      //   warehouse: formData.from_warehouse,
      //   allStocks: stocks.length
      // });

      const stock = stocks.find(s => {
        // Handle both UUID strings and integer IDs
        const productMatch =
          String(s.product) === String(formData.product) ||
          String(s.product_id) === String(formData.product);

        const warehouseMatch =
          String(s.warehouse) === String(formData.from_warehouse) ||
          String(s.warehouse_id) === String(formData.from_warehouse);

        return productMatch && warehouseMatch;
      });
      setAvailableStock(stock ? stock.quantity : 0);
    } else {
      setAvailableStock(null);
    }
  }, [formData.product, formData.from_warehouse, stocks]);

  async function loadData() {
    setLoading(true);
    try {
      const [prods, stocksData, warehousesResponse] = await Promise.all([
        dbHelpers.getProducts(),
        dbHelpers.getStocks(),
        warehousesAPI.getAll()
      ]);

      const warehousesArray = warehousesResponse.data?.results || warehousesResponse.data || [];
      const accessibleWarehouses = filterWarehouses(warehousesArray);


      setProducts(prods);
      setWarehouses(accessibleWarehouses);
      setStocks(stocksData);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }

  async function handleTransfer(e) {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.product || !formData.from_warehouse || !formData.to_warehouse) {
      setError('Please fill all required fields');
      return;
    }

    if (formData.from_warehouse === formData.to_warehouse) {
      setError('Source and destination warehouses must be different');
      return;
    }

    if (formData.quantity <= 0) {
      setError('Quantity must be greater than zero');
      return;
    }

    if (availableStock !== null && formData.quantity > availableStock) {
      setError(`Insufficient stock. Available: ${availableStock} units`);
      return;
    }

    setSubmitting(true);

    try {
      const transferData = {
        product: formData.product,
        from_warehouse: formData.from_warehouse,
        to_warehouse: formData.to_warehouse,
        quantity: parseInt(formData.quantity),
        notes: formData.notes
      };

      // Step 1: Create transfer
      const createResponse = await stockTransfersAPI.create(transferData);

      // Step 2: Execute the transfer
      if (createResponse.data?.id) {
        const executeResponse = await stockTransfersAPI.execute(createResponse.data.id);

        const fromWarehouse = warehouses.find(w => String(w.id) === String(formData.from_warehouse));
        const toWarehouse = warehouses.find(w => String(w.id) === String(formData.to_warehouse));

        setSuccess(`✅ Stock transfer completed! ${formData.quantity} units moved from ${fromWarehouse?.name} to ${toWarehouse?.name}`);

        // Step 3: Force fresh sync from server to update stock quantities
        await syncService.fullSync();

        // Step 4: Reload local data to show updated quantities
        await loadData();
      } else {
        setSuccess('Stock transfer created! Pending execution.');
      }

      // Reset form
      setFormData({
        product: '',
        from_warehouse: '',
        to_warehouse: '',
        quantity: 1,
        notes: ''
      });

      // Navigate after 3 seconds
      setTimeout(() => {
        navigate('/inventory/stocks');
      }, 3000);

    } catch (err) {

      const errorMessage = err.response?.data?.error
        || err.response?.data?.detail
        || err.response?.data?.message
        || (err.response?.data && typeof err.response.data === 'object'
          ? Object.values(err.response.data).flat().join(', ')
          : err.message || 'Failed to create transfer');

      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="stock-transfer-container fade-in">
      <div className="page-header">
        <h1>Stock Transfer</h1>
        <p className="subtitle">Move stock between warehouses</p>
      </div>

      {success && (
        <div className="alert alert-success">
          ✓ {success}
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          ✕ {error}
        </div>
      )}

      <div className="form-card">
        <form onSubmit={handleTransfer}>
          <div className="form-group">
            <label>Product *</label>
            <select
              value={formData.product}
              onChange={(e) => setFormData({ ...formData, product: e.target.value })}
              required
            >
              <option value="">Select Product</option>
              {products.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name} - {p.sku}
                </option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>From Warehouse *</label>
              <select
                value={formData.from_warehouse}
                onChange={(e) => setFormData({ ...formData, from_warehouse: e.target.value })}
                required
              >
                <option value="">Select Source</option>
                {warehouses.map(w => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>To Warehouse *</label>
              <select
                value={formData.to_warehouse}
                onChange={(e) => setFormData({ ...formData, to_warehouse: e.target.value })}
                required
              >
                <option value="">Select Destination</option>
                {warehouses.map(w => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Quantity *</label>
            {availableStock !== null && (
              <small className="help-text">
                Available stock: {availableStock} units
              </small>
            )}
            <input
              type="number"
              value={formData.quantity}
              onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
              min="1"
              max={availableStock || undefined}
              required
            />
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Optional transfer notes..."
              rows="3"
            />
          </div>

          <div className="button-group">
            <button
              type="button"
              onClick={() => navigate('/inventory/stocks')}
              className="btn-secondary"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting ? 'Transferring...' : 'Transfer Stock'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default StockTransfer;
